import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, ZAR, formatApiErrorDetail } from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import {
  Wallet, Sparkles, TrendingUp, ArrowRight, ShoppingBag, AlertTriangle, CheckCircle2,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const fade = {
  hidden: { opacity: 0, y: 20 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.07, duration: 0.4 } }),
};

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [credit, setCredit] = useState(user);
  const [recent, setRecent] = useState([]);
  const [repaying, setRepaying] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = async () => {
    const [c, t] = await Promise.all([api.get("/credit"), api.get("/transactions")]);
    setCredit(c.data.credit);
    setRecent(t.data.transactions.slice(0, 5));
  };

  useEffect(() => { load(); }, []);

  const c = credit || user;
  const limit = c?.credit_limit || 1000;
  const used = c?.credit_used || 0;
  const available = c?.credit_available ?? limit - used;
  const pct = Math.min(100, (used / limit) * 100);

  const repay = async () => {
    setRepaying(true);
    try {
      const { data } = await api.post("/credit/repay");
      setCredit(data.credit);
      await refresh();
      await load();
      setConfirmOpen(false);
      toast.success("Repayment successful — credit reset");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setRepaying(false);
    }
  };

  const status = c?.verification_status || "unverified";
  const canSpend = c?.can_spend;
  const banner = {
    unverified: { text: "Verify your profile — upload your proof of registration and ID copy to unlock credit.", cta: "Upload documents" },
    pending: { text: "Your documents are under review. You'll be able to spend once an admin approves your account.", cta: "View profile" },
    rejected: { text: `Verification rejected: ${c?.rejection_reason || "Please re-upload clearer documents."}`, cta: "Re-upload" },
  }[status];

  return (
    <div>
      <p className="text-xs font-medium tracking-overline text-primary uppercase mb-2">Dashboard</p>
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-8" data-testid="dashboard-greeting">
        Hi, {c?.name?.split(" ")[0] || "Student"}
      </h1>

      {banner && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className={`mb-8 rounded-2xl border-2 p-4 flex items-start sm:items-center justify-between gap-4 flex-col sm:flex-row ${status === "rejected" ? "border-primary bg-primary/5" : "border-sunny bg-sunny/15"}`}
          data-testid="verify-banner"
        >
          <div className="flex items-center gap-3">
            <AlertTriangle className={`${status === "rejected" ? "text-primary" : "text-[#141414]/70"} shrink-0`} size={20} />
            <p className="text-sm" data-testid="verify-banner-text">{banner.text}</p>
          </div>
          <Link
            to="/app/profile"
            data-testid="verify-banner-link"
            className="text-sm font-semibold bg-primary text-white px-4 py-2 rounded-full inline-flex items-center gap-2 shrink-0 hover:-translate-y-0.5 transition-transform duration-150"
          >
            {banner.cta} <ArrowRight size={15} />
          </Link>
        </motion.div>
      )}

      {status === "approved" && (
        <div className="mb-8 inline-flex items-center gap-2 text-sm font-medium text-mint bg-mint/10 px-3 py-1.5 rounded-full" data-testid="approved-chip">
          <CheckCircle2 size={16} /> Your account is verified — credit unlocked.
        </div>
      )}

      {/* Credit card */}
      <motion.div custom={0} variants={fade} initial="hidden" animate="show" className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 rounded-3xl bg-primary text-white p-6 sm:p-8 shadow-[0_10px_40px_rgba(211,23,43,0.25)]" data-testid="credit-card">
          <div className="flex items-start justify-between mb-6">
            <div>
              <p className="text-xs tracking-overline uppercase text-white/70 mb-1">Available credit</p>
              <p className="font-mono text-4xl sm:text-5xl font-bold" data-testid="available-credit">{ZAR(available)}</p>
            </div>
            <Wallet className="text-white" size={28} />
          </div>
          <Progress value={pct} className="h-3 bg-white/20 rounded-full" data-testid="credit-progress" />
          <div className="flex justify-between mt-3 text-sm">
            <span className="text-white/70">
              Used <span className="font-mono text-white" data-testid="credit-used">{ZAR(used)}</span>
            </span>
            <span className="text-white/70">
              Limit <span className="font-mono text-white">{ZAR(limit)}</span>
            </span>
          </div>
        </div>

        <div className="rounded-3xl border-2 border-border bg-card p-6 flex flex-col justify-between" data-testid="repay-card">
          <div>
            <p className="text-xs tracking-overline uppercase text-muted-foreground mb-1">Repayment due</p>
            <p className="font-mono text-3xl font-bold text-primary" data-testid="repay-due">{ZAR(c?.repay_due || 0)}</p>
            <p className="text-xs text-muted-foreground mt-2">
              Flat rate — repay <span className="font-mono">R 1,289</span> on a full <span className="font-mono">R 1,000</span> credit.
            </p>
          </div>
          <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <DialogTrigger asChild>
              <button
                disabled={used <= 0}
                data-testid="repay-btn"
                className="mt-6 w-full bg-primary text-white rounded-full font-semibold py-3 hover:-translate-y-0.5 transition-transform duration-150 disabled:opacity-40 disabled:hover:translate-y-0"
              >
                Repay now
              </button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border rounded-2xl">
              <DialogHeader>
                <DialogTitle>Confirm repayment</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                You're about to repay <span className="font-mono text-primary">{ZAR(c?.repay_due || 0)}</span>.
                This clears your used credit back to zero. (Simulated for this demo.)
              </p>
              <DialogFooter>
                <button
                  onClick={repay}
                  disabled={repaying}
                  data-testid="repay-confirm-btn"
                  className="bg-primary text-white rounded-full font-semibold px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-60"
                >
                  <CheckCircle2 size={16} /> {repaying ? "Processing…" : "Confirm repayment"}
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
        {[
          [Sparkles, "Reward points", (c?.points ?? 0).toLocaleString("en-ZA"), "points-stat"],
          [TrendingUp, "Total spent", ZAR(used), "spent-stat"],
          [Wallet, "Credit limit", ZAR(limit), "limit-stat"],
        ].map(([Icon, label, val, tid], i) => (
          <motion.div
            key={tid} custom={i + 1} variants={fade} initial="hidden" animate="show"
            className="rounded-2xl border-2 border-border bg-card p-5" data-testid={tid}
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-3 ${["bg-sunny/30 text-[#141414]", "bg-mint/20 text-mint", "bg-sky/15 text-sky"][i]}`}>
              <Icon size={18} strokeWidth={2.5} />
            </div>
            <p className="font-mono text-2xl font-bold">{val}</p>
            <p className="text-xs text-muted-foreground mt-1">{label}</p>
          </motion.div>
        ))}
      </div>

      {/* Recent activity */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold tracking-tight">Recent activity</h2>
        <Link to="/app/transactions" className="text-sm text-primary font-medium inline-flex items-center gap-1">
          View all <ArrowRight size={14} />
        </Link>
      </div>
      <div className="rounded-2xl border-2 border-border bg-card divide-y divide-border overflow-hidden" data-testid="recent-activity">
        {recent.length === 0 && (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No transactions yet.{" "}
            <Link to="/app/marketplace" className="text-primary inline-flex items-center gap-1">
              Visit the marketplace <ShoppingBag size={14} />
            </Link>
          </div>
        )}
        {recent.map((t) => (
          <div key={t.id} className="flex items-center justify-between p-4" data-testid={`recent-txn-${t.id}`}>
            <div>
              <p className="text-sm font-medium">{t.description}</p>
              <p className="text-xs text-muted-foreground font-mono">{t.merchant} · {t.reference}</p>
            </div>
            <p className={`font-mono font-bold ${t.type === "repayment" ? "text-muted-foreground" : "text-primary"}`}>
              {t.type === "repayment" ? "-" : "+"}{ZAR(t.amount)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
